import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, ScrollText, Search } from "lucide-react";
import type { AuditTrailItemDto, GetAuditTrailResponse, ApiError } from "@procureflow/shared-types";
import { PageHeader } from "@/components/page-header";
import { fetchWithAuth } from "@/lib/auth-client";
import { buildAuditTrailQuery, type AuditTrailFilters } from "@/lib/audit-trail-query";
import { labelForActionType, labelForEntityType } from "@/mocks/lib/audit-labels";
import { formatDateTime } from "@/lib/format";

// US-050 — Audit trail FE. Replaces the Lovable hardcoded mock with
// a live fetch from GET /api/reports/audit-trail, a filter panel
// (action / entity / actor / correlation), per-row expand to show
// before/after JSON, and paged navigation.
//
// RBAC: route-permissions.ts grants Finance (reports) and Admin
// (settings) access to /audit-logs; the BE controller enforces the
// same boundary via @Roles.
//
// Out of scope (deliberate, follow-up):
//   - URL persistence of filter state
//   - Per-entity drill-down links (e.g. click entity_id → that page)
//   - Correlation banner / CSV export buttons

export const Route = createFileRoute("/audit-logs")({
  head: () => ({
    meta: [
      { title: "Audit Logs — ProcureFlow" },
      {
        name: "description",
        content: "Searchable cross-document audit trail for compliance and security.",
      },
    ],
  }),
  component: Page,
});

const LIMIT = 25;

function Page() {
  const [filters, setFilters] = useState<AuditTrailFilters>({ page: 1, limit: LIMIT });
  const [items, setItems] = useState<AuditTrailItemDto[]>([]);
  const [total, setTotal] = useState(0);
  const [actorOptions, setActorOptions] = useState<{ id: string; name: string }[]>([]);
  const [actionOptions, setActionOptions] = useState<string[]>([]);
  const [entityOptions, setEntityOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (import.meta.env.DEV) {
        const { mswReady } = await import("@/mocks/browser");
        await mswReady;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await fetchWithAuth(
          `/api/reports/audit-trail?${buildAuditTrailQuery(filters)}`,
        );
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as Partial<ApiError>;
          if (!cancelled) setError(body?.message ?? "Failed to load audit trail.");
          return;
        }
        const body = (await res.json()) as GetAuditTrailResponse;
        if (cancelled) return;
        setItems(body.data);
        setTotal(body.meta.total);
        setActorOptions(body.meta.actor_options.map((a) => ({ id: a.id, name: a.name })));
        setActionOptions(body.meta.action_type_options);
        setEntityOptions(body.meta.entity_type_options);
      } catch {
        if (!cancelled) setError("Failed to load audit trail.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [filters]);

  function patchFilter(patch: Partial<AuditTrailFilters>) {
    setFilters((prev) => ({ ...prev, ...patch, page: 1 }));
  }

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="space-y-6">
      <PageHeader description="Immutable record of every action across the platform." />

      <div className="rounded-2xl bg-card border border-border shadow-elegant p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
        <FilterField label="Action">
          <select
            value={filters.action_type ?? ""}
            onChange={(e) => patchFilter({ action_type: e.target.value || undefined })}
            className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm"
          >
            <option value="">All actions</option>
            {actionOptions.map((opt) => (
              <option key={opt} value={opt}>
                {labelForActionType(opt)}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField label="Entity type">
          <select
            value={filters.entity_type ?? ""}
            onChange={(e) => patchFilter({ entity_type: e.target.value || undefined })}
            className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm"
          >
            <option value="">All entities</option>
            {entityOptions.map((opt) => (
              <option key={opt} value={opt}>
                {labelForEntityType(opt)}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField label="Actor">
          <select
            value={filters.actor_id ?? ""}
            onChange={(e) => patchFilter({ actor_id: e.target.value || undefined })}
            className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm"
          >
            <option value="">All actors</option>
            {actorOptions.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField label="Correlation id">
          <div className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="text"
              placeholder="UUID"
              defaultValue={filters.correlation_id ?? ""}
              onBlur={(e) => patchFilter({ correlation_id: e.target.value || undefined })}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  patchFilter({
                    correlation_id: (e.target as HTMLInputElement).value || undefined,
                  });
                }
              }}
              className="w-full bg-transparent py-1.5 text-sm focus:outline-none"
            />
          </div>
        </FilterField>
      </div>

      <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
        {error && (
          <div role="alert" className="border-b border-border px-5 py-3 text-sm text-destructive">
            {error}
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th className="w-6 px-2 py-3" />
              <th className="text-left font-medium px-3 py-3">Timestamp</th>
              <th className="text-left font-medium px-3 py-3">Actor</th>
              <th className="text-left font-medium px-3 py-3">Action</th>
              <th className="text-left font-medium px-3 py-3">Entity</th>
              <th className="text-left font-medium px-5 py-3 font-mono">Correlation</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="px-5 py-10 text-center text-sm text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && !error && (
              <tr>
                <td colSpan={6} className="px-5 py-10 text-center text-sm text-muted-foreground">
                  No audit entries match the current filters.
                </td>
              </tr>
            )}
            {!loading &&
              items.map((row) => {
                const isOpen = expanded.has(row.id);
                return (
                  <ExpandableRow
                    key={row.id}
                    row={row}
                    isOpen={isOpen}
                    onToggle={() => toggleExpanded(row.id)}
                  />
                );
              })}
          </tbody>
        </table>
        <div className="flex items-center justify-between border-t border-border bg-muted/20 px-5 py-3 text-xs text-muted-foreground">
          <p>
            Showing page {filters.page ?? 1} of {totalPages} ({total.toLocaleString()} total)
          </p>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={(filters.page ?? 1) <= 1}
              onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 }))}
              className="rounded border border-border px-2 py-1 hover:bg-background disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={(filters.page ?? 1) >= totalPages}
              onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))}
              className="rounded border border-border px-2 py-1 hover:bg-background disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function ExpandableRow({
  row,
  isOpen,
  onToggle,
}: {
  row: AuditTrailItemDto;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className="border-t border-border hover:bg-accent/40">
        <td className="px-2 py-3.5 align-top">
          <button
            type="button"
            onClick={onToggle}
            aria-label={isOpen ? "Collapse row" : "Expand row"}
            className="text-muted-foreground hover:text-foreground"
          >
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </td>
        <td className="px-3 py-3.5 align-top text-muted-foreground font-mono text-xs">
          {formatDateTime(row.timestamp)}
        </td>
        <td className="px-3 py-3.5 align-top font-medium inline-flex items-center gap-2">
          <ScrollText className="h-4 w-4 text-muted-foreground" />
          {row.actor?.name || "System"}
        </td>
        <td className="px-3 py-3.5 align-top">{labelForActionType(row.action_type)}</td>
        <td className="px-3 py-3.5 align-top text-xs">
          <div>{labelForEntityType(row.entity_type)}</div>
          <div className="font-mono text-muted-foreground">{row.entity_id.slice(0, 8)}…</div>
        </td>
        <td className="px-5 py-3.5 align-top font-mono text-xs text-muted-foreground">
          {row.correlation_id ? `${row.correlation_id.slice(0, 8)}…` : "—"}
        </td>
      </tr>
      {isOpen && (
        <tr className="bg-muted/10">
          <td />
          <td colSpan={5} className="px-3 py-3 text-xs">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div>
                <p className="font-semibold mb-1 text-muted-foreground">Before</p>
                <pre className="rounded bg-background border border-border p-2 overflow-x-auto whitespace-pre-wrap text-[11px]">
                  {row.before_state === null ? "null" : JSON.stringify(row.before_state, null, 2)}
                </pre>
              </div>
              <div>
                <p className="font-semibold mb-1 text-muted-foreground">After</p>
                <pre className="rounded bg-background border border-border p-2 overflow-x-auto whitespace-pre-wrap text-[11px]">
                  {row.after_state === null ? "null" : JSON.stringify(row.after_state, null, 2)}
                </pre>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
